import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/common/PageHeader';
import { getSavedOffers, acceptOffer, unsaveOffer } from '../../services/messageService';
import { getMission } from '../../services/missionService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useI18n } from '../../i18n/I18nContext';

const ClientOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [missionStatuses, setMissionStatuses] = useState({});
  const { t } = useI18n();

  const load = async () => {
    try {
      setLoading(true);
      const data = await getSavedOffers();
      setOffers(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e.message || t('clientOffers.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [t]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener('saved-offers-updated', handler);
    return () => window.removeEventListener('saved-offers-updated', handler);
  }, []);

  useEffect(() => {
    (async () => {
      const toFetch = (offers || [])
        .filter(o => o.status === 'ACCEPTED' && o.missionId && !missionStatuses[o.missionId])
        .map(o => o.missionId);
      for (const mid of toFetch) {
        try {
          const m = await getMission(mid);
          if (m && m.status) {
            setMissionStatuses(prev => ({ ...prev, [mid]: m.status }));
          }
        } catch (_) {}
      }
    })();
  }, [offers, missionStatuses]);

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <ToastContainer position="top-right" autoClose={2500} />
        <PageHeader title={t('clientOffers.title')} subtitle={t('clientOffers.subtitle')} />
        <div className="row g-3">
          {loading && <div className="text-muted p-4">{t('common.loading')}</div>}
          {!loading && error && <div className="alert alert-danger">{error}</div>}
          {!loading && !error && offers.length === 0 && (
            <div className="text-muted p-4">{t('clientOffers.noOffers')}</div>
          )}
          {!loading && !error && offers.map(o => (
            <div className="col-12 col-md-6 col-lg-4" key={o.id}>
              <div 
                className="card h-100 border-0" 
                style={{ 
                  borderRadius: '20px', 
                  cursor: 'pointer', 
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  overflow: 'hidden'
                }} 
                onClick={() => setSelected(o)}
              >
                <div style={{
                  background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
                  color: 'white',
                  padding: '1rem 1.25rem'
                }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-briefcase" style={{ fontSize: '1.2rem' }}></i>
                      <span className="fw-semibold">{o.expediteurEnterpriseName || t('clientOffers.expediteur')}</span>
                    </div>
                    <span className={`badge rounded-pill ${
                      o.status === 'ACCEPTED' ? 'bg-success' :
                      o.status === 'SAVED' ? 'bg-light text-dark' : 'bg-primary'
                    }`}>
                      {o.status === 'ACCEPTED' ? t('clientOffers.status.accepted') : 
                       o.status === 'SAVED' ? t('clientOffers.status.saved') : 
                       o.status}
                    </span>
                  </div>
                </div>
                <div className="card-body d-flex flex-column" style={{ padding: '1.25rem' }}>
                  <div className="mb-2">
                    <div className="small text-muted">{t('clientOffers.route')}</div>
                    <h5 className="mb-0">{o.departureLocation} → {o.arrivalLocation}</h5>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <span className="badge rounded-pill bg-light text-dark border" style={{ fontWeight: 500 }}><i className="bi bi-box-seam me-1"></i>{o.cargoType}</span>
                    <span className="badge rounded-pill bg-light text-dark border"><i className="bi bi-calendar-event me-1"></i>{new Date(o.departureDate).toLocaleDateString()}</span>
                    <span className="badge rounded-pill bg-light text-dark border"><i className="bi bi-truck me-1"></i>{o.vehicleType}</span>
                    {o.status === 'ACCEPTED' && (
                      <span className={`badge rounded-pill ${
                        missionStatuses[o.missionId] === 'EN_COURS' ? 'bg-primary' :
                        missionStatuses[o.missionId] === 'TERMINEE' ? 'bg-success' :
                        missionStatuses[o.missionId] === 'ANNULEE' ? 'bg-danger' : 'bg-warning text-dark'
                      }`}>
                        <i className="bi bi-clipboard2-check me-1"></i>
                        {t('clientOffers.mission')}: {missionStatuses[o.missionId] ? t(`missions.status.${missionStatuses[o.missionId].toLowerCase()}`) : t('missions.status.en_attente')}
                      </span>
                    )}
                  </div>
                  <div className="mt-auto d-flex gap-2">
                    <button className="btn btn-sm btn-outline-secondary" disabled={o.status === 'ACCEPTED'} onClick={async (e) => { e.stopPropagation(); await unsaveOffer(o.id); load(); }}>{t('clientOffers.actions.remove')}</button>
                    <button className="btn btn-sm btn-primary" disabled={o.status === 'ACCEPTED'} onClick={async (e) => { 
                      e.stopPropagation(); 
                      await acceptOffer(o.id); 
                      toast.success(t('clientOffers.success.addedToMissions'));
                      load();
                    }}>{t('clientOffers.actions.addToMissions')}</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {selected && (
        <div className="modal d-block" tabIndex="-1" onClick={() => setSelected(null)}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('clientOffers.modal.title')}</h5>
                <button type="button" className="btn-close" onClick={() => setSelected(null)}></button>
              </div>
              <div className="modal-body">
                <div className="container-fluid">
                  <div className="row mb-2">
                    <div className="col">
                      <div className="small text-muted">{t('common.from')}</div>
                      <div className="fw-semibold">{selected.expediteurEnterpriseName}</div>
                      <div className="text-muted">{selected.expediteurFullName}{selected.expediteurPhone ? ` · ${selected.expediteurPhone}` : ''}</div>
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="small text-muted">{t('common.departure')}</div>
                      <div className="fw-semibold">{selected.departureLocation}</div>
                      <div className="text-muted small">{new Date(selected.departureDate).toLocaleString()}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="small text-muted">{t('common.arrival')}</div>
                      <div className="fw-semibold">{selected.arrivalLocation}</div>
                      <div className="text-muted small">{new Date(selected.arrivalDate).toLocaleString()}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="small text-muted">{t('common.cargo')}</div>
                      <div className="fw-semibold">{selected.cargoType}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="small text-muted">{t('common.vehicle')}</div>
                      <div className="fw-semibold">{selected.vehicleType}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="small text-muted">{t('common.weightLength')}</div>
                      <div className="fw-semibold">{selected.weight} t · {selected.length} m</div>
                    </div>
                    {selected.price != null && (
                      <div className="col-md-4">
                        <div className="small text-muted">{t('common.price')}</div>
                        <div className="fw-semibold">{selected.price}</div>
                      </div>
                    )}
                    {selected.referenceNumber && (
                      <div className="col-md-4">
                        <div className="small text-muted">{t('common.ref')}</div>
                        <div className="fw-semibold">{selected.referenceNumber}</div>
                      </div>
                    )}
                    {selected.description && (
                      <div className="col-12">
                        <div className="small text-muted">{t('common.description')}</div>
                        <div>{selected.description}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setSelected(null)}>{t('common.close')}</button>
                <button type="button" className="btn btn-outline-secondary" disabled={selected.status === 'ACCEPTED'} onClick={async () => { await unsaveOffer(selected.id); setSelected(null); load(); }}>{t('clientOffers.actions.remove')}</button>
                <button type="button" className="btn btn-primary" disabled={selected.status === 'ACCEPTED'} onClick={async () => { await acceptOffer(selected.id); toast.success(t('clientOffers.success.addedToMissions')); setSelected(null); load(); }}>{t('clientOffers.actions.addToMissions')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ClientOffers;